import { ExternalLink, FileText } from "lucide-react";
import { type ReactElement, useMemo } from "react";

import { Badge } from "@/common/ui/badge";
import { cn } from "@/lib/utils";

import type { PrefillActionResponse } from "../types";

const PREVIEW_FIELD_LIMIT = 5;

interface PrefillCardProps {
  action: PrefillActionResponse;
}

function formatFieldValue(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function toBase64(input: string): string {
  const bytes = new TextEncoder().encode(input);
  const binaryString = Array.from(bytes, (byte) => String.fromCodePoint(byte)).join("");
  return btoa(binaryString);
}

function buildPrefillHref(deepLinkUrl: string, payload: Record<string, unknown>[]): string {
  const encoded = encodeURIComponent(toBase64(JSON.stringify(payload)));
  const separator = deepLinkUrl.includes("?") ? "&" : "?";
  return `${deepLinkUrl}${separator}prefill=${encoded}`;
}

export function PrefillCard({ action }: PrefillCardProps): ReactElement {
  const firstEntry = action.payload[0];
  const fieldEntries = firstEntry ? Object.entries(firstEntry) : [];
  const visibleFields = fieldEntries.slice(0, PREVIEW_FIELD_LIMIT);
  const hiddenFieldCount = fieldEntries.length - visibleFields.length;
  const href = useMemo(
    () => buildPrefillHref(action.deepLinkUrl, action.payload),
    [action.deepLinkUrl, action.payload]
  );

  return (
    <div
      className={cn(
        "mt-2 rounded-lg border border-border bg-card",
        "text-card-foreground shadow-sm"
      )}
      data-testid="prefill-card"
    >
      <div className="flex items-center gap-2 border-b border-border px-3 py-2">
        <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <span className="text-xs font-semibold" data-testid="prefill-card-title">
          {action.formTitle}
        </span>
        {action.payload.length > 1 && (
          <Badge
            variant="secondary"
            className="ml-auto text-[10px]"
            data-testid="prefill-card-entry-count"
          >
            {action.payload.length} entries
          </Badge>
        )}
        {action.gaps.length > 0 && (
          <Badge variant="outline" className="text-[10px]" data-testid="prefill-card-gaps">
            {action.gaps.length} {action.gaps.length === 1 ? "gap" : "gaps"}
          </Badge>
        )}
      </div>

      {visibleFields.length > 0 && (
        <div className="px-3 py-2 space-y-1" data-testid="prefill-card-fields">
          {visibleFields.map(([key, value]) => (
            <div
              key={key}
              className="flex items-baseline justify-between gap-2 text-xs"
              data-testid={`prefill-field-${key}`}
            >
              <span className="shrink-0 text-muted-foreground">{key}</span>
              <span className="truncate text-right font-medium">{formatFieldValue(value)}</span>
            </div>
          ))}
          {hiddenFieldCount > 0 && (
            <div className="text-[10px] text-muted-foreground" data-testid="prefill-card-more">
              +{hiddenFieldCount} more
            </div>
          )}
        </div>
      )}

      <div className="border-t border-border px-3 py-2">
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(
            "inline-flex items-center gap-1.5",
            "rounded-md px-2.5 py-1 text-xs font-medium",
            "bg-primary text-primary-foreground",
            "hover:bg-primary/90 transition-colors"
          )}
          data-testid="prefill-card-open"
        >
          Open Form
          <ExternalLink className="h-3 w-3" />
        </a>
      </div>
    </div>
  );
}
